import {Component, OnInit} from '@angular/core';
import {AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {PasswordModule} from 'primeng/password';
import {ButtonModule} from 'primeng/button';
import {CardModule} from 'primeng/card';
import {ToastModule} from 'primeng/toast';
import {MessageService} from 'primeng/api';
import {ChangePasswordService} from '../../services/change-password.service';
import {Router} from '@angular/router';
import {Apollo, gql} from 'apollo-angular';
import {TranslateModule, TranslateService} from '@ngx-translate/core';

@Component({
  selector: 'app-security-settings-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    PasswordModule,
    ButtonModule,
    CardModule,
    ToastModule,
    TranslateModule
  ],
  providers: [
    MessageService
  ],
  templateUrl: './security-settings-form.component.html',
  styleUrl: './security-settings-form.component.scss',
  standalone: true
})
export class SecuritySettingsFormComponent implements OnInit {
  securityForm!: FormGroup;
  specialChars = /[!"$%&}´()*+,\-./:;<=>?@\[\\\]^_{|}~#]/;
  submitting = false;
  showPasswordTips = false;
  lastUpdated: Date | null = null;
  formChanged = false;

  constructor(
    private fb: FormBuilder,
    private changePasswordService: ChangePasswordService,
    private messageService: MessageService,
    private router: Router,
    private apollo: Apollo,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.securityForm.valueChanges.subscribe(() => {
      this.formChanged = true;
    });
  }

  initForm(): void {
    this.securityForm = this.fb.group({
      currentPassword: ['', Validators.required],
      passwordGroup: this.fb.group({
        newPassword: ['', [
          Validators.required,
          Validators.minLength(8),
          this.uppercaseValidator,
          this.specialCharValidator,
          this.usernameValidator
        ]],
        confirmPassword: ['', Validators.required]
      }, { validators: this.passwordMatchValidator })
    });
  }

  onSubmit(): void {
    if (this.securityForm.invalid) {
      this.markFormGroupTouched(this.securityForm);
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('settings.security.toast.validationError.summary'),
        detail: this.translate.instant('settings.security.toast.validationError.detail')
      });
      return;
    }

    this.submitting = true;
    const storedAgent = localStorage.getItem('logged_in_agent');

    if (!storedAgent) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('settings.security.toast.error.summary'),
        detail: this.translate.instant('settings.security.toast.agentNotFound')
      });
      this.submitting = false;
      return;
    }

    const agent = JSON.parse(storedAgent);
    const userEmail = agent.email;
    const { currentPassword, passwordGroup } = this.securityForm.value;

    const rawToken = localStorage.getItem('token') || '';
    let userId: string | null = null;
    try {
      const payload = JSON.parse(atob(rawToken.split('.')[1]));
      const rawUserId = typeof payload.sub === 'object'
        ? payload.sub.id
        : payload.sub;
      userId = rawUserId ? String(rawUserId) : null;
    } catch {
      console.warn('No se pudo decodificar el token JWT');
    }

    if (userId) {
      this.apollo.use('auth').mutate({
        mutation: UPDATE_USER_MUTATION,
        variables: {
          id: userId,
          password: passwordGroup.newPassword
        }
      }).subscribe({
        complete: () => {
          alert(this.translate.instant('settings.security.passwordUpdated'));
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('settings.security.toast.success.summary'),
            detail: this.translate.instant('settings.security.passwordUpdated')
          });
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('settings.security.toast.error.summary'),
            detail: error.error?.message || this.translate.instant('settings.security.toast.updateError')
          });
          this.submitting = false;
        }
      });
      this.securityForm.reset();
      this.initForm();
      this.submitting = false;
      this.lastUpdated = new Date();
      this.formChanged = false;
    }

  }

  // Custom validators
  uppercaseValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) return null;
    return /[A-Z]/.test(value) ? null : { noUppercase: true };
  }

  specialCharValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) return null;
    const specialCharsRegex = /[!"$%&}´()*+,\-./:;<=>?@\[\\\]^_{|}~#]/;
    return specialCharsRegex.test(value) ? null : { noSpecialChar: true };
  }

  usernameValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) return null;

    const agent = JSON.parse(localStorage.getItem('logged_in_agent') || '{}');
    const username = agent.username?.toLowerCase() || '';

    return username && value.toLowerCase().includes(username) ? { containsUsername: true } : null;
  }

  passwordMatchValidator(group: AbstractControl): ValidationErrors | null {
    const password = group.get('newPassword')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;

    return password === confirmPassword ? null : { passwordMismatch: true };
  }

  // Helper methods
  markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  // Getters for template
  get passwordGroup(): FormGroup {
    return this.securityForm.get('passwordGroup') as FormGroup;
  }

  get currentPassword(): AbstractControl {
    return this.securityForm.get('currentPassword')!;
  }

  get newPassword(): AbstractControl {
    return this.passwordGroup.get('newPassword')!;
  }

  get confirmPassword(): AbstractControl {
    return this.passwordGroup.get('confirmPassword')!;
  }

  // Validation helpers for template
  get showPasswordMismatch(): boolean {
    return this.confirmPassword.touched &&
           this.passwordGroup.hasError('passwordMismatch');
  }

  get showMinLengthError(): boolean {
    return this.newPassword.touched &&
           this.newPassword.hasError('minlength');
  }

  get showUppercaseError(): boolean {
    return this.newPassword.touched &&
           this.newPassword.hasError('noUppercase');
  }

  get showSpecialCharError(): boolean {
    return this.newPassword.touched &&
           this.newPassword.hasError('noSpecialChar');
  }

  get showUsernameError(): boolean {
    return this.newPassword.touched &&
           this.newPassword.hasError('containsUsername');
  }

  resetForm(): void {
    this.securityForm.reset();
    this.initForm();
  }

  cancelForm(): void {
    // Navigate to the layout route when cancel is clicked
    this.router.navigate(['/layout']);
  }

  togglePasswordTips(): void {
    this.showPasswordTips = !this.showPasswordTips;
  }

  get passwordStrengthText(): string {
    if (!this.newPassword.value) return '';

    if (this.isStrongPassword) {
      return this.translate.instant('settings.security.strength.strong');
    } else if (this.hasMinLength && this.hasUpperCase && this.hasSpecialChar) {
      return this.translate.instant('settings.security.strength.medium');
    } else {
      return this.translate.instant('settings.security.strength.weak');
    }
  }

  get hasMinLength(): boolean {
    return this.newPassword.value?.length >= 8;
  }

  get hasUpperCase(): boolean {
    return /[A-Z]/.test(this.newPassword.value || '');
  }

  get hasSpecialChar(): boolean {
    return this.specialChars.test(this.newPassword.value || '');
  }

  get isStrongPassword(): boolean {
    const value = this.newPassword.value || '';
    // Additional checks for a strong password
    return this.hasMinLength && this.hasUpperCase && this.hasSpecialChar &&
           value.length >= 12 && /\d/.test(value) && !this.hasCommonPatterns(value);
  }

  hasCommonPatterns(password: string): boolean {
    // Check for common patterns like sequential numbers, repeated characters, etc.
    const sequential = '1234567890qwertyuiopasdfghjklzxcvbnm';

    // Check for 3+ sequential characters
    for (let i = 0; i < sequential.length - 3; i++) {
      const seq = sequential.substring(i, i + 3);
      if (password.toLowerCase().includes(seq)) {
        return true;
      }
    }

    // Check for 3+ repeated characters
    for (let i = 0; i < password.length - 2; i++) {
      if (password[i] === password[i+1] && password[i] === password[i+2]) {
        return true;
      }
    }

    return false;
  }

  // Add method to get password strength icon
  getStrengthIcon(): string {
    if (!this.newPassword.value) return 'pi-circle';

    if (this.isStrongPassword) {
      return 'pi-shield';
    } else if (this.hasMinLength && this.hasUpperCase && this.hasSpecialChar) {
      return 'pi-check-circle';
    } else {
      return 'pi-exclamation-circle';
    }
  }

  // Add getter for username containment check
  get containsUsername(): boolean {
    if (!this.newPassword.value) return false;

    const agent = JSON.parse(localStorage.getItem('logged_in_agent') || '{}');
    const username = agent.username?.toLowerCase() || '';

    return username && this.newPassword.value.toLowerCase().includes(username);
  }

  // Enhanced method for getting password strength class
  get passwordStrengthClass(): string {
    if (!this.newPassword.value) return '';

    const passwordValue = this.newPassword.value;
    let score = 0;

    // Check length
    if (passwordValue.length >= 8) score += 1;
    if (passwordValue.length >= 12) score += 1;

    // Check character types
    if (/[A-Z]/.test(passwordValue)) score += 1;
    if (this.specialChars.test(passwordValue)) score += 1;
    if (/\d/.test(passwordValue)) score += 1;

    // Check for common patterns
    if (this.hasCommonPatterns(passwordValue)) score -= 1;

    // Check for username
    if (this.containsUsername) score -= 1;

    // Return appropriate class
    if (score >= 4) return 'strong';
    if (score >= 2) return 'medium';
    return 'weak';
  }
}
const UPDATE_USER_MUTATION = gql`
  mutation updateUser($id: ID!, $password: String!) {
    updateUser(
      id: $id
      input: { password: $password }
    ) {
      id
    }
  }
`;
